"use client";

import * as React from "react";
import { PanelLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsDesktop, useIsMobile } from "@/lib/use-is-mobile";
import { Button } from "./button";
import { Separator } from "./separator";
import { Sheet, SheetContent, SheetTitle } from "./sheet";

const SIDEBAR_COOKIE = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

// CargoFL ShadCN Library — Sidebar primitive set (pragmatic, self-contained).
type SidebarContextValue = {
  /** The user's preference: expanded vs. icon rail. Not the same as actually being expanded. */
  open: boolean;
  /**
   * Whether the sidebar is *actually* showing labels right now: `open` AND at `lg`+, or inside
   * the mobile drawer. Between `md` and `lg` an "open" sidebar is still a rail, so JS branches
   * (logo vs. mark, tooltips on/off) must use this, not `open`.
   */
  expanded: boolean;
  setOpen: (open: boolean) => void;
  /** Toggles the drawer on mobile and the rail on desktop — one trigger drives both. */
  toggle: () => void;
  isMobile: boolean;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
};

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}

interface SidebarProviderProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const SidebarProvider = React.forwardRef<HTMLDivElement, SidebarProviderProps>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
    const [openMobile, setOpenMobile] = React.useState(false);
    const isMobile = useIsMobile();
    const isDesktop = useIsDesktop();
    const open = openProp ?? internalOpen;
    // Inside the drawer the sidebar always shows labels; on the tablet rail it never does.
    const expanded = isMobile ? true : open && isDesktop;

    const setOpen = React.useCallback(
      (value: boolean) => {
        if (onOpenChange) {
          onOpenChange(value);
        } else {
          setInternalOpen(value);
        }
        // Persisted so the server can render the correct width on the next request —
        // otherwise the sidebar always mounts expanded and visibly snaps shut.
        document.cookie = `${SIDEBAR_COOKIE}=${value}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}; samesite=lax`;
      },
      [onOpenChange]
    );

    const toggle = React.useCallback(
      () => (isMobile ? setOpenMobile((v) => !v) : setOpen(!open)),
      [isMobile, open, setOpen]
    );

    const value = React.useMemo<SidebarContextValue>(
      () => ({ open, expanded, setOpen, toggle, isMobile, openMobile, setOpenMobile }),
      [open, expanded, setOpen, toggle, isMobile, openMobile]
    );

    return (
      <SidebarContext.Provider value={value}>
        <div
          ref={ref}
          className={cn("flex min-h-svh w-full", className)}
          {...props}
        >
          {children}
        </div>
      </SidebarContext.Provider>
    );
  }
);
SidebarProvider.displayName = "SidebarProvider";

const Sidebar = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement>
>(({ className, children, ...props }, ref) => {
  const { expanded, isMobile, openMobile, setOpenMobile } = useSidebar();

  // Below `md` the sidebar leaves the layout entirely and becomes an overlay drawer — a 64px
  // rail would eat 17% of a phone screen and never show a label. `data-state="open"` +
  // `group/sidebar` make the consumer render its expanded form inside the drawer unchanged.
  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          side="left"
          data-state="open"
          className="group/sidebar w-[264px] border-sidebar-border bg-sidebar p-0 text-sidebar-foreground"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="flex h-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      ref={ref}
      // Reflects `expanded`, not `open`: between `md` and `lg` an "open" sidebar is still a
      // 64px rail, and its labels must stay hidden. Drives the
      // `group-data-[state=closed]/sidebar:` hooks consumers use to hide labels and centre rows.
      data-state={expanded ? "open" : "closed"}
      data-collapsible={expanded ? "" : "icon"}
      className={cn(
        // Pinned to the viewport: sticky + full viewport height so the nav/footer stay in view
        // while the main content scrolls. Without this the aside stretches to the (tall) page
        // height and its bottom-anchored footer (logout) falls below the fold.
        // Collapsed state is an icon rail (w-16), matching shadcn's collapsible="icon" — not a
        // full offcanvas hide. Consumers use the `group-data-[state=closed]` hooks to swap the
        // logo for the mark, hide labels, and center rows.
        "group/sidebar sticky top-0 flex h-svh flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-in-out",
        // `max-md:hidden` keeps this from flashing for one frame on a phone before `isMobile`
        // resolves in an effect. Tablets (md–lg) get a rail even when open: 768px minus a 256px
        // sidebar leaves too little room for the data tables.
        "max-md:hidden",
        expanded ? "w-64" : "w-16",
        className
      )}
      {...props}
    >
      {children}
    </aside>
  );
});
Sidebar.displayName = "Sidebar";

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentPropsWithoutRef<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { toggle } = useSidebar();
  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      className={cn("h-7 w-7", className)}
      onClick={(event) => {
        onClick?.(event);
        toggle();
      }}
      {...props}
    >
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
});
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-2 p-2", className)}
    {...props}
  />
));
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("mt-auto flex flex-col gap-2 p-2", className)}
    {...props}
  />
));
SidebarFooter.displayName = "SidebarFooter";

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-2",
      className
    )}
    {...props}
  />
));
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("relative flex w-full flex-col p-2", className)}
    {...props}
  />
));
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70",
      className
    )}
    {...props}
  />
));
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("w-full text-sm", className)}
    {...props}
  />
));
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex w-full flex-col gap-1", className)}
    {...props}
  />
));
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn("group/menu-item relative", className)}
    {...props}
  />
));
SidebarMenuItem.displayName = "SidebarMenuItem";

interface SidebarMenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  isActive?: boolean;
}

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  SidebarMenuButtonProps
>(({ asChild = false, isActive = false, className, children, ...props }, ref) => {
  const classes = cn(
    "flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0",
    // Icon rail: centre the icon and hide the text label. Keyed off `data-state` on the sidebar
    // group, which the Sidebar component resolves per context (rail below `lg`, drawer always
    // open) — so this is correct at every width without needing its own breakpoints.
    "group-data-[state=closed]/sidebar:justify-center group-data-[state=closed]/sidebar:[&>span]:hidden",
    isActive &&
      "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
    className
  );

  if (asChild) {
    return (
      <Slottable className={classes} childrenProp={children} />
    );
  }

  return (
    <button ref={ref} className={classes} {...props}>
      {children}
    </button>
  );
});
SidebarMenuButton.displayName = "SidebarMenuButton";

// Minimal asChild support without pulling Slot typing complexity into the menu button.
function Slottable({
  className,
  childrenProp,
}: {
  className: string;
  childrenProp: React.ReactNode;
}) {
  const child = React.Children.only(childrenProp) as React.ReactElement<{
    className?: string;
  }>;
  return React.cloneElement(child, {
    className: cn(className, child.props.className),
  });
}

const SidebarInset = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <main
    ref={ref}
    className={cn(
      // `min-w-0` is load-bearing: as a flex child this would otherwise default to
      // `min-width: auto` and refuse to shrink below its widest descendant, so a wide
      // table (or any non-wrapping row) inflates the whole inset past the viewport
      // instead of scrolling inside its own `overflow-x-auto`.
      "relative flex min-h-svh min-w-0 flex-1 flex-col bg-background",
      className
    )}
    {...props}
  />
));
SidebarInset.displayName = "SidebarInset";

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentPropsWithoutRef<typeof Separator>
>(({ className, ...props }, ref) => (
  <Separator
    ref={ref}
    className={cn("mx-2 w-auto bg-sidebar-border", className)}
    {...props}
  />
));
SidebarSeparator.displayName = "SidebarSeparator";

export {
  SidebarProvider,
  Sidebar,
  SidebarTrigger,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarInset,
  SidebarSeparator,
  useSidebar,
};
